import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableScroller,
} from '@/components/ui/table';
import { formatLocalDateTime } from '@/lib/datetime';
import { messages } from '@/i18n';
import { useApi } from '@/api/apiContext';
import { ApiError } from '@/api/errors';
import {
  customerShipmentQueryOptions,
  groundHandlerShipmentQueryOptions,
  shipmentQueryOptions,
  type ShipmentScope,
} from '@/api/endpoints/shipments';
import type {
  CustomerShipmentDetail,
  GroundHandlerShipmentDetail,
  ShipmentDetail,
  ShipmentStatus,
} from '@/types/shipment';
import { DownloadNoaButton } from '@/screens/driver/collectionsComponents';
import { STATUS_BADGE_CLASS, shipmentStatusBadgeClass } from './shipmentsData';

type AnyShipmentDetail = ShipmentDetail | GroundHandlerShipmentDetail | CustomerShipmentDetail;

// A named type guard rather than an inline `'packages' in shipment` check —
// the latter does not narrow `shipment`'s type here (TanStack Query's
// unioned generics collapse it before the `in` check can see it).
function hasPackages(
  shipment: AnyShipmentDetail,
): shipment is ShipmentDetail | CustomerShipmentDetail {
  return 'packages' in shipment;
}

// Same reasoning as hasPackages: a named guard for the customer scope's other
// missing field, rather than an inline check that TanStack Query's generics
// would collapse before it could narrow anything.
function hasCustomerName(
  shipment: AnyShipmentDetail,
): shipment is ShipmentDetail | GroundHandlerShipmentDetail {
  return 'customerName' in shipment;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs font-medium">{label}</p>
      <p className="mt-0.5 text-sm">{children}</p>
    </div>
  );
}

export function ShipmentDetailScreen({ scope = 'tollit' }: { scope?: ShipmentScope }) {
  const t = messages.shipmentDetail;
  const statusLabels = messages.packageStatus;
  const shipmentStatusLabels = messages.shipmentStatus;
  const api = useApi();

  const { awb } = useParams<{ awb: string }>();
  // Three hooks, not one hook fed a unioned `queryOptions` — the schemas (and
  // so the inferred data types) genuinely differ, and `enabled` keeps only
  // the one matching `scope` actually fetching. Calling all three keeps this
  // a fixed number of hooks per render regardless of `scope`.
  const tollitQuery = useQuery({
    ...shipmentQueryOptions(api, awb ?? ''),
    enabled: Boolean(awb) && scope === 'tollit',
  });
  const handlerQuery = useQuery({
    ...groundHandlerShipmentQueryOptions(api, awb ?? ''),
    enabled: Boolean(awb) && scope === 'handler',
  });
  const customerQuery = useQuery({
    ...customerShipmentQueryOptions(api, awb ?? ''),
    enabled: Boolean(awb) && scope === 'customer',
  });
  const shipmentQuery =
    scope === 'handler' ? handlerQuery : scope === 'customer' ? customerQuery : tollitQuery;
  // Explicitly annotated: read through the unioned `shipmentQuery` above,
  // TanStack Query's generics collapse `.data` to `unknown` instead of the
  // `ShipmentDetail | GroundHandlerShipmentDetail | CustomerShipmentDetail` union.
  const shipment: AnyShipmentDetail | undefined =
    scope === 'handler'
      ? handlerQuery.data
      : scope === 'customer'
        ? customerQuery.data
        : tollitQuery.data;
  const notFound =
    shipmentQuery.isError &&
    shipmentQuery.error instanceof ApiError &&
    shipmentQuery.error.status === 404;

  const backLink = (
    <Link
      to=".."
      className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1.5 text-sm"
    >
      <ArrowLeft className="size-4" aria-hidden />
      {t.backToShipments}
    </Link>
  );

  if (shipmentQuery.isLoading) {
    return (
      <section className="mx-auto max-w-2xl">
        {backLink}
        <p className="text-muted-foreground text-sm">{t.loading}</p>
      </section>
    );
  }

  if (shipmentQuery.isError && !notFound) {
    return (
      <section className="mx-auto max-w-2xl">
        {backLink}
        <Alert variant="destructive">
          <Info aria-hidden />
          <AlertDescription>{t.loadError}</AlertDescription>
        </Alert>
      </section>
    );
  }

  if (!shipment) {
    return (
      <section className="mx-auto max-w-2xl">
        {backLink}
        <Card>
          <CardContent className="pt-6">
            <h1 className="text-lg font-semibold">{t.shipmentNotFoundTitle}</h1>
            <p className="text-muted-foreground mt-1 text-sm">{t.shipmentNotFoundBody}</p>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-5xl">
      {backLink}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">{shipment.transportDocumentNo}</h1>
        <span className="text-muted-foreground text-sm">
          {hasCustomerName(shipment) ? `${shipment.customerName ?? '—'} · ` : null}
          {shipment.flightNo}
        </span>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t.summaryTitle}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 pt-0 sm:grid-cols-3">
          {hasCustomerName(shipment) ? (
            <Field label={t.summaryCustomer}>{shipment.customerName ?? '—'}</Field>
          ) : null}
          <Field label={t.summaryAwb}>{shipment.transportDocumentNo}</Field>
          <Field label={t.summaryManifestNo}>{shipment.manifestNo ?? '—'}</Field>
          <Field label={t.summaryFlightNo}>{shipment.flightNo}</Field>
          <Field label={t.summaryStatus}>
            <Badge className={shipmentStatusBadgeClass(shipment.status)}>
              {shipmentStatusLabels[shipment.status as ShipmentStatus] ?? shipment.status}
            </Badge>
          </Field>
          <Field label={t.summaryEtd}>{formatLocalDateTime(shipment.etd)}</Field>
          <Field label={t.summaryEta}>{formatLocalDateTime(shipment.eta)}</Field>
          <Field label={t.summaryTotalNetWeight}>{shipment.totalNetWeight} kg</Field>
          <Field label={t.summaryTotalBoxes}>{shipment.totalNoOfBoxes}</Field>
          <Field label={t.summaryPalletScanned}>{shipment.palletScannedOrder ?? '—'}</Field>
          <Field label={t.summaryNoOfParcels}>{shipment.noOfParcels}</Field>
          <Field label={t.summarySubmittedOrder}>{shipment.submittedOrder}</Field>
          <Field label={t.summaryNoaDateTime}>{formatLocalDateTime(shipment.noaDateTime)}</Field>
          <Field label={t.summaryNoaQuantity}>{shipment.noaQuantity ?? '—'}</Field>
          <Field label={t.summaryCustomsClearedAt}>
            {formatLocalDateTime(shipment.customsClearedAt)}
          </Field>
          <Field label={t.summaryDriverCollected}>{shipment.driverCollected ?? '—'}</Field>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t.noasTitle}</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {shipment.noas.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t.noasEmpty}</p>
          ) : (
            <TableScroller label={t.noasTitle}>
              <Table columnCount={6} className="min-w-[640px]">
                <TableHeader>
                  <tr>
                    <TableHead>{t.noasPart}</TableHead>
                    <TableHead>{t.noasReceived}</TableHead>
                    <TableHead align="right">{t.noasPieces}</TableHead>
                    <TableHead align="right">{t.noasCollected}</TableHead>
                    <TableHead>{t.noasStatus}</TableHead>
                    <TableHead>{t.noasDocument}</TableHead>
                  </tr>
                </TableHeader>
                <TableBody>
                  {shipment.noas.map((part) => (
                    <TableRow key={part.id}>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {part.partNumber}
                      </TableCell>
                      <TableCell muted className="whitespace-nowrap tabular-nums">
                        {formatLocalDateTime(part.noaDateTime)}
                      </TableCell>
                      <TableCell muted numeric>
                        {part.pieces ?? '—'}
                      </TableCell>
                      <TableCell muted numeric>
                        {part.collectedUnits ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={part.collectionStatus === 'Collected' ? 'default' : 'outline'}
                        >
                          {part.collectionStatus === 'Collected'
                            ? t.noasStatusCollected
                            : t.noasStatusReady}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {part.hasDocument ? (
                          <DownloadNoaButton
                            id={part.id}
                            awb={shipment.transportDocumentNo}
                            partNumber={part.partNumber}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableScroller>
          )}
        </CardContent>
      </Card>

      {hasPackages(shipment) ? (
        <Card>
          <CardHeader>
            <CardTitle>{t.packagesTitle}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {shipment.packages.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t.packagesEmpty}</p>
            ) : (
              <TableScroller label={t.packagesTitle}>
                <Table columnCount={4} className="min-w-[640px]">
                  <TableHeader>
                    <tr>
                      <TableHead>{t.packagesOrderNo}</TableHead>
                      <TableHead>{t.packagesStatus}</TableHead>
                      <TableHead align="right">{t.packagesColli}</TableHead>
                      <TableHead>{t.packagesClearanceDate}</TableHead>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {shipment.packages.map((pkg) => (
                      <TableRow key={pkg.orderNo}>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {pkg.orderNo}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_BADGE_CLASS[pkg.packageStatus]}>
                            {statusLabels[pkg.packageStatus]}
                          </Badge>
                        </TableCell>
                        <TableCell muted numeric>
                          {pkg.totalNoOfColli}
                        </TableCell>
                        <TableCell muted className="whitespace-nowrap tabular-nums">
                          {formatLocalDateTime(pkg.clearanceDate)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableScroller>
            )}
          </CardContent>
        </Card>
      ) : null}
    </section>
  );
}
